import { MouseEvent, useCallback, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { noop } from '@gif/shared-global';
import { ModalCloseEvent, ModalProps } from './ModalProps';
import { CloseIcon } from '../../assets';

const StyledModal = styled.dialog<{ $removeParentPadding?: boolean }>`
  border: none;
  border-radius: 0.5rem;
  padding: ${(props) => (props.$removeParentPadding ? 0 : '1rem')};
  width: 80%;
  max-width: 23.45rem;
  &::backdrop {
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const ModalBody = styled.div<{ $addBodyPadding?: boolean }>`
  padding: ${(props) => (props.$addBodyPadding ? '1rem' : 0)};
`;

const CloseButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
  background-color: white;

  svg {
    height: 1.25rem;
    width: 1.25rem;
  }

  &:hover {
    cursor: pointer;
  }
`;

const ModalHeader = styled.header`
  display: flex;
  justify-content: end;
`;

export const Modal = ({
  isOpen = false,
  testId,
  children,
  id,
  className,
  customHeader = null,
  showCloseIcon,
  closeButtonAria = 'Dismiss modal',
  closeButtonAlt = 'Close',
  onClose = noop,
  onOpen = noop,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isOpenRef = useRef(isOpen);
  const onCloseHandler = useCallback(
    (event: ModalCloseEvent) => {
      onClose(event);
      dialogRef.current?.close();
    },
    [onClose]
  );
  // handles native html close behavior
  useEffect(() => {
    if (isOpenRef.current === isOpen || isOpen) {
      return;
    }
    onCloseHandler(null);
  }, [isOpen, onCloseHandler]);
  // handles native html open behavior
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    onOpen();
    dialogRef.current?.showModal();
  }, [isOpen, onOpen]);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const onModalChildWrapperClickHandler = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const onModalClickHandler = useCallback(
    (event: MouseEvent<Element>) => {
      if (!dialogRef.current) {
        return;
      }
      const rect = dialogRef.current.getBoundingClientRect();
      const isClickedInDialog =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;
      if (!isClickedInDialog) {
        onCloseHandler(event);
      }
    },
    [onCloseHandler]
  );

  return (
    <StyledModal
      id={id}
      className={className}
      data-testid={testId}
      ref={dialogRef}
      onCancel={onCloseHandler}
      onClick={onModalClickHandler}
      $removeParentPadding={!!customHeader}
    >
      {customHeader}
      <ModalBody
        className="modal-body"
        onClick={onModalChildWrapperClickHandler}
        $addBodyPadding={!!customHeader}
      >
        {!customHeader && showCloseIcon && (
          <ModalHeader>
            <CloseButton
              role="button"
              className="closeModalIcon"
              aria-label={closeButtonAria}
              onClick={onCloseHandler}
              {...(testId && { 'data-testid': `${testId}-closeModalIcon` })}
            >
              <CloseIcon />
              {/* <CloseIcon title={closeButtonAlt} /> */}
            </CloseButton>
          </ModalHeader>
        )}
        {children}
      </ModalBody>
    </StyledModal>
  );
};

export default Modal;
